import React, { useEffect, useState } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Box,
  Flex,
  ModalFooter,
  Divider,
} from '@chakra-ui/react';
import { format, getDay, getMonth, getYear } from 'date-fns';
import { ByWeekday, Frequency, RRule, Weekday } from 'rrule';

import { Cross, CustomReccurenceIcon } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';

import { Button } from 'uiToolkitV1/Button';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { Select } from 'uiToolkitV1/Select';
import { TextBodyMedium, TextHeader3, TextLabel } from 'uiToolkitV1/Typography';

import { StyleCloseButton } from './ArchiveMeetingModal';

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
  onSave?: (recurrenceRule: string) => void;
  selectedDate?: Date;
  recurrenceRule?: string;
}

const RECURRENCE_FREQUENCY_OPTIONS = [
  {
    label: 'Week',
    value: RRule.WEEKLY,
  },
  {
    label: 'Month',
    value: RRule.MONTHLY,
  },
];

const RECURRENCE_FREQUENCY_OPTIONS_V1 = [
  {
    label: 'Weeks',
    value: RRule.WEEKLY,
  },
  {
    label: 'Months',
    value: RRule.MONTHLY,
  },
];

const RECURRENCE_DAY_OPTIONS = [
  {
    label: 'Monday',
    value: RRule.MO,
  },
  {
    label: 'Tuesday',
    value: RRule.TU,
  },
  {
    label: 'Wednesday',
    value: RRule.WE,
  },
  {
    label: 'Thursday',
    value: RRule.TH,
  },
  {
    label: 'Friday',
    value: RRule.FR,
  },
  {
    label: 'Saturday',
    value: RRule.SA,
  },
  {
    label: 'Sunday',
    value: RRule.SU,
  },
];

function getDatesOfDayOfWeek(year: number, month: number, dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
  const initialDate = new Date(year, month, 1);
  const datesOfDayOfWeek: number[] = [];

  while (initialDate.getDay() !== dayOfWeek) {
    initialDate.setDate(initialDate.getDate() + 1);
  }

  while (initialDate.getMonth() === month) {
    const nextDate = new Date(initialDate.getTime());
    datesOfDayOfWeek.push(nextDate.getDate());
    initialDate.setDate(initialDate.getDate() + 7);
  }

  return datesOfDayOfWeek;
}

export const getWhatDayOfTheMonth = (date: Date) => {
  const day = getDay(date);
  const month = getMonth(date);
  const year = getYear(date);

  const datesOfDayOfWeek = getDatesOfDayOfWeek(year, month, day);

  const nthDayOfTheMonth = datesOfDayOfWeek.indexOf(date.getDate()) + 1;
  return { nthDayOfTheMonth, isLastNthDayOfTheMonth: nthDayOfTheMonth === datesOfDayOfWeek.length };
};

const numberToWords = {
  1: 'First',
  2: 'Second',
  3: 'Third',
  4: 'Fourth',
  5: 'Fifth',
};

const generateMonthlyRecurrenceOptions = (date: Date) => {
  const { nthDayOfTheMonth, isLastNthDayOfTheMonth } = getWhatDayOfTheMonth(date);
  const selectedMonthDay = date.getDate();
  const selectedDay = RRule[format(date, 'EEEEEE').toUpperCase()] as Weekday;

  const RECURRENCE_MONTH_OPTIONS = [
    {
      label: `Monthly on day ${selectedMonthDay}`,
      value: selectedMonthDay,
      isByMonthDay: true,
    },
    {
      label: `Monthly on the ${numberToWords[nthDayOfTheMonth]} ${format(date, 'EEEE')}`,
      value: selectedDay.nth(nthDayOfTheMonth),
    },
  ];

  if (nthDayOfTheMonth === 5) {
    RECURRENCE_MONTH_OPTIONS.pop();
  }

  if (isLastNthDayOfTheMonth) {
    RECURRENCE_MONTH_OPTIONS.push({
      label: `Monthly on the last ${format(date, 'EEEE')}`,
      value: selectedDay.nth(-1),
    });
  }

  return RECURRENCE_MONTH_OPTIONS;
};

const CustomRecurrenceModal = ({
  isOpen = false,
  onClose,
  onSave,
  selectedDate = new Date(),
  recurrenceRule,
}: Props) => {
  const selectedDay = RRule[format(selectedDate, 'EEEEEE').toUpperCase()] as Weekday;

  // const selectedDayNumber = selectedDate.getDate();

  const RECURRENCE_MONTH_OPTIONS = generateMonthlyRecurrenceOptions(selectedDate);

  const rRule = RRule.fromString(recurrenceRule);

  // whether every 1 week or 2 weeks etc or every 1 month or 2 months etc
  const [interval, setInterval] = useState(rRule.options.interval ?? 1);
  // whether monthly | weekly => 1 | 2
  const [frequency, setFrequency] = useState(rRule.options.freq ?? RRule.WEEKLY);
  // whether monday or tuesday etc
  const [byDay, setByDay] = useState<ByWeekday | ByWeekday[]>();
  // whether 1st or 2nd or 3rd or 4th or 5th
  const [byMonthDay, setByMonthDay] = useState<number>(rRule.options.bymonthday[0] ?? null);
  // const [byMonthDay, setByMonthDay] = useState<number>(selectedDayNumber);

  const onIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInterval(parseInt(e.target.value));
  };

  const onFrequencyChange = ({ value }: { value: Frequency }) => {
    setFrequency(value);
    if (value === RRule.MONTHLY) {
      setByMonthDay(selectedDate.getDate());
      setByDay(null);
    } else {
      setByMonthDay(null);
      setByDay(selectedDay);
    }
  };

  //
  useEffect(() => {
    // checking if bymondayday is null or not
    if (rRule.options.bymonthday?.[0]) {
      setByMonthDay(selectedDate.getDate());
      setByDay(null);
    } else {
      setByMonthDay(null);
      /* setting day to selected Date day. Date takes week start from Sunday and we are starting from Monday in array. */
      setByDay([RECURRENCE_DAY_OPTIONS[(7 + selectedDate.getDay() - 1) % 7].value]);
    }
  }, [selectedDate]);

  const onDayChange = ({ value, isByMonthDay = false }: { value: Weekday | number; isByMonthDay?: boolean }) => {
    if (isByMonthDay && typeof value === 'number') {
      setByMonthDay(value);
      setByDay(null);
    } else if (!isByMonthDay && typeof value !== 'number') {
      setByMonthDay(null);
      setByDay(value);
    }
  };

  const selectedByDay = RECURRENCE_DAY_OPTIONS.find((option) => option.value?.toString() === byDay?.toString());

  const selectedByMonthDay = byMonthDay
    ? RECURRENCE_MONTH_OPTIONS.find((option) => option.value === byMonthDay)
    : RECURRENCE_MONTH_OPTIONS.find((option) => option.value?.toString() === byDay?.toString());

  const selectedFrequency =
    interval > 1
      ? RECURRENCE_FREQUENCY_OPTIONS_V1.find((option) => option.value === frequency)
      : RECURRENCE_FREQUENCY_OPTIONS.find((option) => option.value === frequency);

  const generateRRule = () => {
    const rRule = new RRule({
      freq: frequency,
      interval,
      byweekday: byDay,
      bymonthday: byMonthDay,
    });
    onSave(rRule.toString().split('RRULE:')[1]);
  };

  const closeModalHandler = () => {
    setInterval(rRule.options.interval ?? 1);
    setFrequency(rRule.options.freq ?? RRule.WEEKLY);
    setByDay(rRule.origOptions.byweekday);
    setByMonthDay(rRule.options.bymonthday[0] ?? null);
    onClose();
  };

  return (
    // REFER: https://github.com/JedWatson/react-select/issues/4262#issuecomment-738559015
    <Modal isOpen={isOpen} onClose={closeModalHandler} returnFocusOnClose={false}>
      <ModalOverlay />
      <ModalContent position="relative" maxH="204px" maxW={isMobileView() ? '300px' : '578px'}>
        <ModalHeader position="relative">
          <Flex alignItems="center">
            <CustomReccurenceIcon boxSize="24px" color="#666666" mr="8px" />
            <TextHeader3 bold>Custom recurrence</TextHeader3>
          </Flex>

          <StyleCloseButton
            variant="filled"
            color="black"
            StartIcon={(props) => <Cross {...props} boxSize="20px" />}
            onClick={onClose}
          />
        </ModalHeader>
        <Divider opacity={1} color="#EBEFF3" />

        <ModalBody p="26px 30px">
          <Box display="flex" ml="3px">
            <Flex mr="30px" flexDir="column">
              <TextLabel>Repeat every</TextLabel>
              <Flex>
                <InputV2 mr="12px" w="70px" type="number" min={1} onChange={onIntervalChange} value={interval} />

                <Flex
                  minW="100px"
                  sx={{
                    '.select': {
                      width: '100%',
                      height: '100%',
                      borderBottom: '1px solid #999999',
                    },
                    '.chakra-input__group': {
                      height: '100%',
                      width: '100%',
                    },
                    '.select__control': {
                      height: '100%',
                      width: '100%',
                    },
                    '& .select__single-value': {
                      fontWeight: 'normal', // Override the font weight for the selected value
                      margin: '8px 0px 10px 0px',
                    },
                  }}
                  h="100%"
                >
                  {/* @ts-ignore */}
                  <Select
                    options={RECURRENCE_FREQUENCY_OPTIONS}
                    handleChange={onFrequencyChange}
                    value={selectedFrequency || RECURRENCE_FREQUENCY_OPTIONS[0]}
                  />
                </Flex>
              </Flex>
            </Flex>
            <TextBodyMedium m="21px 20px 0px -10px">on</TextBodyMedium>
            <Flex w="100%" flexDir="column">
              {/* <TextLabel>Select days</TextLabel> */}
              <Flex
                sx={{
                  '.select': {
                    width: '100%',
                    borderBottom: '1px solid #999999',
                    // height: '100%'
                  },
                  '.chakra-input__group': {
                    // height: '100%'
                  },
                  '.select__control': {
                    // height: '100%'
                  },
                  '& .select__single-value': {
                    fontWeight: 'normal', // Override the font weight for the selected value
                    margin: '10px 0px 13px 0px',
                  },
                }}
                // h="100%"
                mt="11px"
              >
                {/* @ts-ignore */}
                <Select
                  options={frequency === RRule.MONTHLY ? RECURRENCE_MONTH_OPTIONS : RECURRENCE_DAY_OPTIONS}
                  handleChange={onDayChange}
                  value={frequency === RRule.MONTHLY ? selectedByMonthDay : selectedByDay}
                  isDisabled={frequency === RRule.WEEKLY}
                />
              </Flex>
            </Flex>
          </Box>
        </ModalBody>
        <ModalFooter
          sx={{
            background: '#F5F7F9',
            borderTop: '1px solid #CED7DF',
            borderRadius: '0px 0px 8px 8px',
          }}
        >
          <Button
            sx={{
              '&>p': {
                marginLeft: 0,
              },
            }}
            mr="auto"
            variant="tertiary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={generateRRule} disabled={interval <= 0 || Number.isNaN(interval)}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { CustomRecurrenceModal };
